import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Video, ZypePlayerData } from '../types';
import { MediaService } from '../services/api';
import { Skeleton } from './Skeleton';

interface VideoPlayerProps {
    video?: Video;
    isLoading?: boolean;
    pwsHost?: string;
    showSlug?: string;
    ajaxUrl?: string; // Keep for backward compatibility but ignore
    nonce?: string; // Keep for backward compatibility but ignore
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
    video,
    isLoading = false,
    pwsHost = 'services.premierenetworks.com',
    showSlug = 'rush'
    // Ignore ajaxUrl and nonce - not used in direct API approach
}) => {
    const [playerLoading, setPlayerLoading] = useState(false);
    const [playerError, setPlayerError] = useState<string | null>(null);
    const [zypePlayerData, setZypePlayerData] = useState<ZypePlayerData | null>(null);
    const [playerUrl, setPlayerUrl] = useState<string | null>(null);

    // Use simplified MediaService like the original plugin
    const mediaService = useMemo(() => new MediaService(pwsHost, showSlug), [pwsHost, showSlug]);

    // Construct Zype player embed URL with API key
    const constructZypePlayerUrl = useCallback((videoId: string, apiKey: string) => {
        const params = new URLSearchParams({
            api_key: apiKey,
            autoplay: 'false',
            controls: 'true',
        });

        return `https://player.zype.com/embed/${videoId}.html?${params.toString()}`;
    }, []);

    // Load Zype player data and setup player
    const loadZypePlayer = useCallback(async (video: Video) => {
        if (!video.id && !video.zype_id) {
            setPlayerError('No video ID available for Zype player');
            return;
        }

        setPlayerLoading(true);
        setPlayerError(null);

        try {
            console.log(`🎬 Getting Zype player data for video: ${video.id || video.zype_id}`);

            // Use video.id or fallback to zype_id
            const videoId = video.id || video.zype_id!;
            const playerData = await mediaService.getZypePlayerData(videoId, false);

            console.log('🔑 Zype player data:', playerData);
            setZypePlayerData(playerData);

            if (!playerData.playerApiKey) {
                throw new Error('No player API key received');
            }

            // Construct Zype player embed URL
            const embedUrl = constructZypePlayerUrl(videoId, playerData.playerApiKey);
            console.log('📺 Zype player URL:', embedUrl);
            setPlayerUrl(embedUrl);

        } catch (error) {
            console.error('Failed to load Zype player:', error);
            setPlayerError(`Failed to load video player: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            // Add a small delay to prevent flickering
            setTimeout(() => {
                setPlayerLoading(false);
            }, 500);
        }
    }, [mediaService, constructZypePlayerUrl]);

    // Load video when video prop changes
    useEffect(() => {
        if (video) {
            console.log(`🎬 Loading video: ${video.title}`);

            // Reset states
            setPlayerUrl(null);
            setPlayerError(null);
            setZypePlayerData(null);

            // Try Zype player first (preferred method)
            if (video.id || video.zype_id) {
                loadZypePlayer(video);
            }
            // Method 2: Use existing embed URL
            else if (video.embed_url) {
                console.log('📺 Using existing embed URL');
                setPlayerUrl(video.embed_url);
                setPlayerError(null);
            }
            // Fallback: Show error for unsupported videos
            else {
                setPlayerError('Video format not supported');
                console.warn('⚠️ No compatible video source found for:', video.title);
            }
        }
    }, [video, loadZypePlayer]);

    const handleRetry = () => {
        if (video) {
            setPlayerError(null);
            loadZypePlayer(video);
        }
    };

    // Show loading skeleton for initial load
    if (isLoading || !video) {
        return (
            <div className="component-zype-player" style={{width: '648px', height: '250px'}}>
                <Skeleton shape="16x9" />
                <div style={{marginTop: '10px'}}>
                <Skeleton shape="6x3" />
                </div>
            </div>
        );
    }

    // Show error state
    if (playerError) {
        return (
            <div className="component-zype-player">
                <div style={{
                    backgroundColor: '#333',
                    padding: '40px',
                    textAlign: 'center',
                    color: '#ff6b6b',
                    borderRadius: '4px'
                }}>
                    <h3>⚠️ Video Error</h3>
                    <p>{playerError}</p>
                    {process.env.NODE_ENV === 'development' && (
                        <div style={{ fontSize: '12px', color: '#aaa', marginTop: '10px' }}>
                            <p>Video ID: {video.id || video.zype_id || 'N/A'}</p>
                            {playerUrl && <p>Player URL: {playerUrl.substring(0, 80)}...</p>}
                            {zypePlayerData && <p>API Key: {zypePlayerData.playerApiKey.substring(0, 20)}...</p>}
                        </div>
                    )}
                    <button
                        onClick={handleRetry}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#06203a',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginTop: '10px'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
       <div className="component-zype-player" style={{ width: '100%' }}>
  <div
    style={{
      position: 'relative',
      width: '100%',
      maxWidth: '628px',
      backgroundColor: '#000',
      aspectRatio: '628 / 350', // fallback added below
    }}
  >
    <div
      style={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.32%', // fallback for older browsers
      }}
    >
                {/* Always render iframe container to prevent flickering */}
                {playerUrl ? (
                    <iframe
                        src={playerUrl}
                        width="100%"
                        height="100%"
                        style={{
                            border: 'none',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            opacity: playerLoading ? 0 : 1,
                            transition: 'opacity 0.3s ease-in-out'
                        }}
                        allowFullScreen
                        allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                        title={video.title}
                    />
                ) : (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#333',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#666',
                        fontSize: '18px'
                    }}>
                        {playerLoading ? 'Loading Media...' : 'Select a video to play'}
                    </div>
                )}

                {/* Loading overlay with smooth transition */}
                {playerLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        zIndex: 10,
                        transition: 'opacity 0.3s ease-in-out'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ marginBottom: '10px' }}>⏳</div>
                            <div>Loading Media...</div>
                        </div>
                    </div>
                )}
            </div>
            </div>

            {/* {process.env.NODE_ENV === 'development' && (
                <div style={{
                    padding: '10px',
                    backgroundColor: '#333',
                    fontSize: '12px',
                    color: '#aaa'
                }}>
                    🎬 <strong>Video:</strong> {video.title}
                    <br />
                    📺 <strong>Player:</strong> Zype Player (Bitmovin Engine)
                    <br />
                    🆔 <strong>Video ID:</strong> {video.id || video.zype_id || 'N/A'}
                    {zypePlayerData && (
                        <>
                            <br />
                            🔑 <strong>API Key:</strong> {zypePlayerData.playerApiKey.substring(0, 20)}...
                            <br />
                            🎵 <strong>Audio Only:</strong> {zypePlayerData.isAudio ? 'Yes' : 'No'}
                            <br />
                            🔐 <strong>Authenticated:</strong> {zypePlayerData.authenticated ? 'Yes' : 'No'}
                        </>
                    )}
                    {playerUrl && (
                        <>
                            <br />
                            🔗 <strong>Player URL:</strong> {playerUrl.substring(0, 60)}...
                        </>
                    )}
                </div>
            )} */}
        </div>
    );
}; 